/**
 * @file     AuthGateController.tsx
 * @purpose  Drop-in mount that decides whether to render the AuthGateModal on
 *           the current page. Reads auth state + viewed-listing count from
 *           localStorage and renders the modal if the unauth user has crossed
 *           VIEWED_LISTINGS_THRESHOLD. The modal is dismissible on the
 *           /properties list page and suppressed on public detail pages.
 */
'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useAuth } from '@/hooks/useAuth';
import { useViewedListings } from '@/hooks/useViewedListings';
import { isGateEligible } from '@/hooks/useListingsAccess';
import { OVERLAY_PRIORITY, useOverlaySlot } from '@/core/overlays/OverlayPriorityProvider';
import { AuthGateModal } from './AuthGateModal';

export const AuthGateController = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const { hasReachedThreshold } = useViewedListings();
  const pathname = usePathname();
  const [dismissed, setDismissed] = useState(false);
  // Shared floor: no sign-up wall before the full allowance of properties
  // has been seen. Read after mount so the server render
  // and first paint never gate.
  const [pastFloor, setPastFloor] = useState(false);
  useEffect(() => {
    setPastFloor(isGateEligible());
  }, [pathname]);

  const isListPage = pathname === '/properties';
  const isDetailPage = pathname?.startsWith('/properties/') ?? false;
  // Detail pages: never hard-gate. PremiumPanelGate wraps the intelligence
  // panels with their own inline lock so basic browsing (price, address,
  // gallery, mortgage calc, BCRES brokerage attribution) stays free.
  // List page: still dismissible.
  const wantsToShow =
    !isLoading &&
    !isAuthenticated &&
    pastFloor &&
    hasReachedThreshold &&
    !isDetailPage &&
    !(isListPage && dismissed);

  // Highest passive priority — the auth gate beats the ToU banner so they never
  // stack (arbitrated centrally by the overlay provider).
  const canShow = useOverlaySlot('auth-gate', OVERLAY_PRIORITY.authGate, wantsToShow);
  if (!canShow) return null;

  return (
    <AuthGateModal
      open={true}
      onDismiss={isListPage ? () => setDismissed(true) : undefined}
    />
  );
};
